import * as React from 'react'
import { Loader2, UserPlus } from 'lucide-react'
import type { TeamInvitationDto } from '@lib/dto/team.dto'
import { tenantRoleSchema, type TenantRole } from '@lib/enums/tenant-role.enum'
import { inviteMemberInputSchema, type InviteMemberInput } from '@lib/input/team.input'
import { Button } from '@/components/ui/button'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { ROLE_DESCRIPTIONS, ROLE_LABELS, ROLE_OPTIONS } from './roles'

interface InviteFormProps {
  defaultRole?: TenantRole
  onInvite: (input: InviteMemberInput) => Promise<TeamInvitationDto>
}

/** Email + role invitation form. Confirms with a toast and clears itself. */
export function InviteForm({ defaultRole = 'developer', onInvite }: InviteFormProps) {
  const [email, setEmail] = React.useState('')
  const [role, setRole] = React.useState<TenantRole>(defaultRole)
  const [error, setError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const parsed = inviteMemberInputSchema.safeParse({ email, role })
    if (!parsed.success) {
      setError('Enter a valid email address.')
      return
    }
    setIsSubmitting(true)
    try {
      await onInvite(parsed.data)
      toast.success(`Invitation sent to ${parsed.data.email}`)
      setEmail('')
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not send the invitation.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="flex flex-col gap-3 sm:flex-row sm:items-start" onSubmit={handleSubmit} noValidate>
      <Field label="Email" htmlFor="invite-email" error={error} className="flex-1">
        <Input
          id="invite-email"
          type="email"
          autoComplete="off"
          placeholder="teammate@example.com"
          value={email}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? fieldMessageId('invite-email') : undefined}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field label="Role" htmlFor="invite-role" hint={ROLE_DESCRIPTIONS[role]} className="sm:w-56">
        <Select
          id="invite-role"
          value={role}
          aria-describedby={fieldMessageId('invite-role')}
          onChange={(event) => {
            const next = tenantRoleSchema.safeParse(event.target.value)
            if (next.success) setRole(next.data)
          }}
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {ROLE_LABELS[option]}
            </option>
          ))}
        </Select>
      </Field>
      <Button
        type="submit"
        disabled={isSubmitting}
        // Line up with the inputs: one label line (control text, leading-none) plus the Field gap.
        className="sm:mt-[calc(var(--ds-control-text)+0.375rem)]"
      >
        {isSubmitting ? <Loader2 className="animate-spin" /> : <UserPlus />}
        Send invite
      </Button>
    </form>
  )
}
